import {
	ActivityDateGroup,
	ActivityEntry,
	ActivityStatus,
	Agent,
	AgentDetail,
	AgentDetailItem,
	AgentInvocationDetailItem,
	AgentInvocationItem,
	AgentListItem,
	AgentStatus,
	AgentTimeEntry,
	AgentTriggerType,
	BuilderMode,
	Cadence,
	CadenceTime,
	ExecutionHistoryEntry,
	ExecutionStatus,
	isCadence,
	NotificationRuleItem,
	RuleConfigDetailsItem,
	RuleConfigItem,
	ScheduleTriggerDetails,
	TriggerConfig,
} from '@trovata/app/features/ai-agents/models/agents.model';

// ==========================================
// Local Types
// ==========================================

/** 12-hour format time representation returned by `convert24To12`. */
interface TwelveHourTime {
	readonly hour: number;
	readonly ampm: 'am' | 'pm';
}

// ==========================================
// API → UI Mapping Functions
// ==========================================

/** Day-of-week index (0=Sun..6=Sat) to lowercase name. */
const DAY_INDEX_TO_NAME: readonly string[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** Lowercase day name to day-of-week index (0=Sun..6=Sat). */
const DAY_NAME_TO_INDEX: Readonly<Record<string, number>> = {
	sunday: 0,
	monday: 1,
	tuesday: 2,
	wednesday: 3,
	thursday: 4,
	friday: 5,
	saturday: 6,
};

/**
 * Converts a 24-hour value to 12-hour format with AM/PM.
 *
 * @param hour24 - Hour in 24-hour format (0-23)
 * @returns Object with 12-hour value and ampm indicator
 */
function convert24To12(hour24: number): TwelveHourTime {
	if (hour24 === 0) {
		return { hour: 12, ampm: 'am' };
	}
	if (hour24 === 12) {
		return { hour: 12, ampm: 'pm' };
	}
	if (hour24 > 12) {
		return { hour: hour24 - 12, ampm: 'pm' };
	}
	return { hour: hour24, ampm: 'am' };
}

/**
 * Converts a 12-hour value with AM/PM to 24-hour format.
 *
 * @param hour - Hour in 12-hour format (1-12)
 * @param ampm - AM or PM indicator
 * @returns Hour in 24-hour format (0-23)
 */
function convert12To24(hour: number, ampm: 'am' | 'pm'): number {
	if (ampm === 'am') {
		return hour === 12 ? 0 : hour;
	}
	return hour === 12 ? 12 : hour + 12;
}

/**
 * Maps a NotificationRuleItem from the API to a UI TriggerConfig.
 * Hydrates all times from the API response and converts to 12-hour format.
 * Returns default empty config if no rule is present.
 *
 * @param rule - Notification rule from the API response, or null
 * @returns TriggerConfig for the config form
 */
export function mapRuleToTriggerConfig(rule: NotificationRuleItem | null): TriggerConfig {
	if (!rule?.ruleConfig?.details) {
		return {};
	}

	const details: RuleConfigDetailsItem = rule.ruleConfig.details;
	const cadence: Cadence | undefined = isCadence(details.cadence) ? details.cadence : undefined;

	if (!cadence) {
		return {
			type: rule.ruleType === 'scheduled_agent_trigger' ? AgentTriggerType.scheduled : undefined,
		};
	}

	const times: CadenceTime[] = details.cadenceDetails?.times ?? [];
	const timezone: string = rule.ruleConfig.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

	let scheduleDetails: ScheduleTriggerDetails;

	switch (cadence) {
		case Cadence.hourly:
			scheduleDetails = {
				cadence: Cadence.hourly,
				minute: times[0]?.minute ?? 0,
				timezone,
			};
			break;

		case Cadence.daily:
			scheduleDetails = {
				cadence: Cadence.daily,
				times: times.map((t: CadenceTime, idx: number) => {
					const converted: TwelveHourTime = convert24To12(t.hour ?? 0);
					return { id: idx + 1, hour: converted.hour, minute: t.minute, ampm: converted.ampm };
				}),
				timezone,
			};
			break;

		case Cadence.weekly: {
			const days: string[] = (times[0]?.daysOfWeek ?? []).map((d: number) => DAY_INDEX_TO_NAME[d]).filter(Boolean);
			scheduleDetails = {
				cadence: Cadence.weekly,
				days,
				times: times.map((t: CadenceTime, idx: number) => {
					const converted: TwelveHourTime = convert24To12(t.hour ?? 0);
					return { id: idx + 1, hour: converted.hour, minute: t.minute, ampm: converted.ampm };
				}),
				timezone,
			};
			break;
		}

		default: {
			const _exhaustive: never = cadence;
			throw new Error(`Unhandled Cadence: ${JSON.stringify(_exhaustive)}`);
		}
	}

	return {
		type: rule.ruleType === 'scheduled_agent_trigger' ? AgentTriggerType.scheduled : undefined,
		cadence,
		scheduleDetails,
	};
}

// ==========================================
// UI → API Mapping Functions
// ==========================================

/**
 * Maps a UI TriggerConfig to a RuleConfigItem for the PATCH API body.
 * Converts 12-hour UI times to 24-hour API format.
 * Returns null if the trigger is not fully configured.
 *
 * @param config - TriggerConfig from the config form
 * @returns RuleConfigItem for the PATCH body, or null if not configured
 */
export function mapTriggerConfigToRuleConfig(config: TriggerConfig): RuleConfigItem | null {
	if (!config.type || !config.cadence || !config.scheduleDetails) {
		return null;
	}

	const details: ScheduleTriggerDetails = config.scheduleDetails;
	let times: CadenceTime[];
	let timezone: string | null = null;

	switch (details.cadence) {
		case Cadence.hourly:
			timezone = details.timezone;
			times = [{ daysOfWeek: null, hour: null, minute: details.minute }];
			break;

		case Cadence.daily:
			timezone = details.timezone;
			times = details.times.map((t: AgentTimeEntry) => ({
				daysOfWeek: null,
				hour: convert12To24(t.hour, t.ampm),
				minute: t.minute,
			}));
			break;

		case Cadence.weekly: {
			timezone = details.timezone;
			const daysOfWeek: number[] = details.days.map((d: string) => DAY_NAME_TO_INDEX[d]).filter((n: number) => n !== undefined);
			times = details.times.map((t: AgentTimeEntry) => ({
				daysOfWeek,
				hour: convert12To24(t.hour, t.ampm),
				minute: t.minute,
			}));
			break;
		}

		default: {
			const _exhaustive: never = details;
			throw new Error(`Unhandled ScheduleTriggerDetails: ${JSON.stringify(_exhaustive)}`);
		}
	}

	return {
		name: null,
		cron: null,
		timezone,
		details: {
			cadence: config.cadence,
			cadenceDetails: { times },
			accountIds: null,
			conditions: null,
		},
		deliveryTypes: ['EMAIL'],
	};
}

// ==========================================
// Delivery (Recipients ↔ Emails) Mapping
// ==========================================

/**
 * Splits the raw, comma-separated delivery input into a normalized email array
 * for the agent `emails` PATCH field. Trims each entry and drops empty ones, so
 * trailing commas and incidental whitespace don't produce blank recipients.
 *
 * @param recipients - Raw comma-separated string as typed in the delivery field
 * @returns Trimmed, non-empty email addresses in input order
 */
export function parseRecipients(recipients: string): string[] {
	return recipients
		.split(',')
		.map((email: string) => email.trim())
		.filter((email: string) => email.length > 0);
}

/**
 * Joins the agent `emails` array back into the comma-separated string shown in
 * the delivery input when an existing agent is loaded.
 *
 * @param emails - Email distribution list from the agent entity
 * @returns Comma-and-space separated string for the delivery field
 */
export function formatRecipients(emails: readonly string[]): string {
	return emails.join(', ');
}

/**
 * Practical email shape: `local@domain.tld`. Mirrors the spirit of Angular's
 * `Validators.email` — rejects missing `@`, missing TLD, stray `@`, and
 * whitespace — without the full RFC 5322 complexity that rejects rare-but-valid
 * forms.
 */
const EMAIL_PATTERN: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Returns the recipient entries that are not valid email addresses.
 *
 * Reuses `parseRecipients` so validation runs against exactly what gets
 * persisted (same comma-split, trimming, and empty-entry dropping). An empty or
 * whitespace-only input yields no invalid entries, since recipients are optional.
 *
 * @param recipients - Raw comma-separated string from the delivery field
 * @returns The malformed entries, in input order; empty when all entries are valid
 */
export function findInvalidRecipients(recipients: string): string[] {
	return parseRecipients(recipients).filter((email: string) => !EMAIL_PATTERN.test(email));
}

// ==========================================
// Status Derivation
// ==========================================

/**
 * Derives the agent UI status from the authoritative `paused` flag and rule presence.
 *
 * The API also returns a `status` string, but it's hard-coded to `"success"`
 * server-side as an HTTP-level marker — it never reflects lifecycle state, so
 * `paused` + `rule` are the only signals that matter here.
 *
 * @param paused - Whether the agent is currently paused
 * @param rule - The notification rule from the API, or null
 * @returns Paused if `paused` is true; Active if a rule exists; Draft otherwise
 */
export function deriveAgentStatus(paused: boolean, rule: NotificationRuleItem | null): AgentStatus {
	if (paused) {
		return AgentStatus.paused;
	}
	return rule ? AgentStatus.active : AgentStatus.draft;
}

// ==========================================
// Builder Create-vs-Update Semantic
// ==========================================

/**
 * Whether the builder is working on a not-yet-persisted agent: `new` mode
 * outright, or `continue` mode before the drafted agent has ever been saved
 * (no persisted status exists yet). Drives both the save-menu option set and
 * create-vs-update analytics attribution, so the semantic lives in one place
 * and cannot drift between the two.
 *
 * @param mode - Builder dialog mode, or null when the dialog is closed
 * @param status - The selected agent's persisted status, or null when none exists
 * @returns True when saving would create the agent rather than update it
 */
export function isNewAgentBuild(mode: BuilderMode | null, status: AgentStatus | null): boolean {
	return mode === 'new' || (mode === 'continue' && !status);
}

// ==========================================
// Invocation Status Vocabulary
// ==========================================

/** How one raw server status renders on the two pill surfaces (activity feed, execution history). */
interface InvocationStatusVocabularyEntry {
	readonly activityStatus: ActivityStatus;
	readonly executionStatus: ExecutionStatus;
}

/**
 * The single raw-status → pill vocabulary for every surface that renders a run's
 * status (activity feed items, activity detail header, agent execution history,
 * and the AI-chat side panel through the same components). One table means a run
 * cannot show different states on different surfaces mid-flight.
 *
 * `running` and `pending` are the backend's non-terminal statuses — they must
 * always read as in-progress, never as a finished No Action.
 */
const INVOCATION_STATUS_VOCABULARY: Readonly<Record<string, InvocationStatusVocabularyEntry>> = {
	success: { activityStatus: ActivityStatus.success, executionStatus: ExecutionStatus.success },
	completed: { activityStatus: ActivityStatus.success, executionStatus: ExecutionStatus.success },
	no_action: { activityStatus: ActivityStatus.noAction, executionStatus: ExecutionStatus.noAction },
	warning: { activityStatus: ActivityStatus.warning, executionStatus: ExecutionStatus.warning },
	pending: { activityStatus: ActivityStatus.pending, executionStatus: ExecutionStatus.pending },
	running: { activityStatus: ActivityStatus.pending, executionStatus: ExecutionStatus.pending },
	error: { activityStatus: ActivityStatus.alert, executionStatus: ExecutionStatus.error },
	failed: { activityStatus: ActivityStatus.alert, executionStatus: ExecutionStatus.error },
};

/**
 * The server does not whitelist statuses (the field is a plain string on its model),
 * so unknowns are expected. An unknown reads as a terminal No Action — safe for
 * text the vocabulary has never seen, and never reached by the non-terminal
 * statuses, which the vocabulary lists explicitly.
 */
const UNKNOWN_STATUS_ENTRY: InvocationStatusVocabularyEntry = {
	activityStatus: ActivityStatus.noAction,
	executionStatus: ExecutionStatus.noAction,
};

/** Maps a raw server status to its activity feed pill semantic. */
export function toActivityStatus(rawStatus: string): ActivityStatus {
	return (INVOCATION_STATUS_VOCABULARY[rawStatus] ?? UNKNOWN_STATUS_ENTRY).activityStatus;
}

/** Maps a raw server status to its execution history badge semantic. */
export function toExecutionStatus(rawStatus: string): ExecutionStatus {
	return (INVOCATION_STATUS_VOCABULARY[rawStatus] ?? UNKNOWN_STATUS_ENTRY).executionStatus;
}

/**
 * Whether a raw server status means the run has finished — the answer the invocation
 * poll ends on.
 *
 * Read off the same vocabulary the pills render from, unknown fallback included, so
 * the poll and the screen cannot disagree. When they did, an unknown status showed as
 * a finished pill while the poll kept waiting on that run for its whole budget and
 * then claimed it was still going.
 *
 * @param rawStatus - Status string exactly as the API returned it
 * @returns True for every status that is not one of the in-progress ones
 */
export function isTerminalInvocationStatus(rawStatus: string): boolean {
	return toActivityStatus(rawStatus) !== ActivityStatus.pending;
}

/**
 * Pill label for an activity feed status. Lives here, next to the vocabulary,
 * so no surface carries its own label switch that can drift.
 *
 * @param status - Semantic feed status from `toActivityStatus`
 * @returns The label the pill renders
 */
export function activityStatusLabel(status: ActivityStatus): string {
	switch (status) {
		case ActivityStatus.warning:
			return 'Warning';
		case ActivityStatus.alert:
			return 'Alert';
		case ActivityStatus.noAction:
			return 'No Action';
		case ActivityStatus.success:
			return 'Success';
		case ActivityStatus.pending:
			return 'Pending';
		default: {
			const exhaustive: never = status;
			return exhaustive;
		}
	}
}

/**
 * Badge label for an execution history status — the history counterpart of
 * `activityStatusLabel`.
 *
 * @param status - Semantic history status from `toExecutionStatus`
 * @returns The label the badge renders
 */
export function executionStatusLabel(status: ExecutionStatus): string {
	switch (status) {
		case ExecutionStatus.success:
			return 'Success';
		case ExecutionStatus.noAction:
			return 'No Action';
		case ExecutionStatus.error:
			return 'Error';
		case ExecutionStatus.pending:
			return 'Pending';
		case ExecutionStatus.warning:
			return 'Warning';
		default: {
			const exhaustive: never = status;
			return exhaustive;
		}
	}
}

// ==========================================
// Invocations → Execution History Mapping
// ==========================================

/** Short excerpt length when falling back from an empty title. */
const INVOCATION_EXCERPT_LENGTH: number = 100;

/** Builds a short description: explicit title, else first N chars of the response, else empty string. */
function titleOrExcerpt(title: string | null | undefined, response: string | null | undefined): string {
	return title || response?.substring(0, INVOCATION_EXCERPT_LENGTH) || '';
}

/**
 * Maps API AgentInvocationItem[] to UI ExecutionHistoryEntry[].
 *
 * @param invocations - Invocation records from the agent detail API response
 * @returns Execution history entries for the UI
 */
export function mapInvocationsToHistory(invocations: readonly AgentInvocationItem[]): ExecutionHistoryEntry[] {
	return invocations.map((inv: AgentInvocationItem) => ({
		timestamp: inv.createdAt ?? '',
		status: toExecutionStatus(inv.status),
		description: titleOrExcerpt(inv.title, inv.response),
	}));
}

/**
 * Creates an optimistic ExecutionHistoryEntry for a pending manual invocation.
 * Used to give immediate UI feedback before polling returns real data.
 *
 * @returns A pending execution history entry with the current timestamp
 */
export function createPendingHistoryEntry(): ExecutionHistoryEntry {
	return {
		timestamp: new Date().toISOString(),
		status: ExecutionStatus.pending,
		description: 'Execution in progress\u2026',
	};
}

// ==========================================
// Invocations → Activity Feed Mapping
// ==========================================

/**
 * Maps one invocation to an activity feed entry.
 *
 * Shared with the single-invocation detail route so a run reached by deep link renders identically
 * to the same run picked out of the feed.
 *
 * @param inv - One invocation from GET /agents/invocations or /agents/invocations/{id}
 * @returns The activity feed entry for that run
 */
export function mapInvocationToActivityEntry(inv: AgentInvocationDetailItem): ActivityEntry {
	return {
		id: inv.invocationId,
		agentId: inv.agent.agentId,
		agentName: inv.agent.name,
		title: titleOrExcerpt(inv.title, inv.response),
		status: toActivityStatus(inv.status),
		timestamp: inv.createdAt ?? '',
		content: inv.response,
	};
}

/**
 * Maps cross-agent invocations to date-grouped activity feed entries.
 * Groups by calendar date, sorted descending. Entries within each group
 * sorted by timestamp descending.
 *
 * @param invocations - Recent invocations from GET /agents/invocations
 * @returns Activity feed grouped by date for the Activity tab
 */
export function mapInvocationsToActivityGroups(invocations: readonly AgentInvocationDetailItem[]): ActivityDateGroup[] {
	if (!invocations.length) {
		return [];
	}

	const grouped: Map<string, ActivityEntry[]> = new Map();
	const today: Date = new Date();
	today.setHours(0, 0, 0, 0);
	const yesterday: Date = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);

	for (const inv of invocations) {
		const dateKey: string = inv.createdAt ? formatDateLabel(new Date(inv.createdAt), today, yesterday) : 'Unknown';
		const group: ActivityEntry[] = grouped.get(dateKey) ?? [];
		group.push(mapInvocationToActivityEntry(inv));
		grouped.set(dateKey, group);
	}

	const groups: ActivityDateGroup[] = Array.from(grouped.entries()).map(([date, entries]: [string, ActivityEntry[]]) => ({
		date,
		entries: entries.sort((a: ActivityEntry, b: ActivityEntry) => (b.timestamp > a.timestamp ? 1 : b.timestamp < a.timestamp ? -1 : 0)),
	}));

	// Sort groups descending by the most recent entry timestamp in each group
	groups.sort((a: ActivityDateGroup, b: ActivityDateGroup) => {
		const aTime: string = a.entries[0]?.timestamp ?? '';
		const bTime: string = b.entries[0]?.timestamp ?? '';
		return bTime > aTime ? 1 : bTime < aTime ? -1 : 0;
	});

	return groups;
}

/** Formats a date as "Today", "Yesterday", or "M/D/YYYY". */
function formatDateLabel(date: Date, today: Date, yesterday: Date): string {
	const d: Date = new Date(date);
	d.setHours(0, 0, 0, 0);
	if (d.getTime() === today.getTime()) {
		return 'Today';
	}
	if (d.getTime() === yesterday.getTime()) {
		return 'Yesterday';
	}
	return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

// ==========================================
// API → AgentDetail Mapping (No Mock Data)
// ==========================================

/**
 * Builds an AgentDetail directly from an AgentListItem API response.
 * Uses only real API data. Fields not supported by the API fall back to
 * empty string or null placeholders — never mock data.
 *
 * @param agent - The base Agent (identifier, sessionId, name, prompt)
 * @param item - The raw API list item
 * @returns AgentDetail built entirely from API data
 */
export function mapListItemToAgentDetail(agent: Agent, item: AgentListItem): AgentDetail {
	const triggerConfig: TriggerConfig = item.rule ? mapRuleToTriggerConfig(item.rule) : {};
	return {
		...agent,
		status: deriveAgentStatus(item.paused, item.rule),
		type: triggerConfig.type ?? AgentTriggerType.scheduled,
		description: '',
		emails: item.emails ?? [],
		lastRun: item.lastRunTimestamp ?? null,
		nextRun: null,
		lastModified: item.updatedAt ?? null,
		createdOn: item.createdAt ?? null,
		triggers: item.rule ? 1 : 0,
		triggerConfig,
		executionHistory: [],
	};
}

/**
 * Builds an AgentDetail directly from an AgentDetailItem API response.
 * Includes invocations mapped to execution history. Uses only real API data.
 *
 * @param agent - The base Agent (identifier, sessionId, name, prompt)
 * @param detail - The raw API detail item
 * @returns AgentDetail built entirely from API data
 */
export function mapDetailItemToAgentDetail(agent: Agent, detail: AgentDetailItem): AgentDetail {
	const triggerConfig: TriggerConfig = detail.rule ? mapRuleToTriggerConfig(detail.rule) : {};
	return {
		...agent,
		status: deriveAgentStatus(detail.paused, detail.rule),
		type: triggerConfig.type ?? AgentTriggerType.scheduled,
		description: '',
		emails: detail.emails ?? [],
		lastRun: detail.lastRunTimestamp ?? null,
		nextRun: null,
		lastModified: detail.updatedAt ?? null,
		createdOn: detail.createdAt ?? null,
		triggers: detail.rule ? 1 : 0,
		triggerConfig,
		executionHistory: mapInvocationsToHistory(detail.invocations || []),
	};
}
